import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Globe } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * Language switcher with flag icons.
 *
 * Uses real flag PNGs from flagcdn.com so Windows / older Android where the
 * emoji flag glyphs don't render still show a proper flag. `w40` gives a
 * 40-pixel wide asset — plenty for our 22-24px display size.
 */
const FLAG = (cc) => `https://flagcdn.com/w40/${cc}.png`;
const Flag = ({ cc, alt, size = 22 }) => (
  <img
    src={FLAG(cc)}
    alt={alt}
    width={size}
    height={Math.round(size * 0.75)}
    loading="lazy"
    className="inline-block object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
  />
);

const UI_LANGS = [
  { code: "nl", cc: "nl", label: "Nederlands" },
  { code: "en", cc: "gb", label: "English" },
  { code: "ar", cc: "sa", label: "العربية" },
];

const AI_LANGS = [
  { code: "de", cc: "de", label: "Deutsch" },
  { code: "fr", cc: "fr", label: "Français" },
  { code: "es", cc: "es", label: "Español" },
  { code: "pl", cc: "pl", label: "Polski" },
  { code: "tr", cc: "tr", label: "Türkçe" },
];

export default function LanguageToggle({ testId = "language-toggle" }) {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = UI_LANGS.find((l) => l.code === lang) || UI_LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref} data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-btn`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors"
      >
        <Flag cc={current.cc} alt={current.label} size={20} />
        <span className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-neutral-700">
          {current.code}
        </span>
        <ChevronDown size={12} strokeWidth={1.8} className={`text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid={`${testId}-menu`}
          className="absolute right-0 top-full mt-1 z-50 w-64 border border-neutral-900 bg-white shadow-xl"
        >
          <div className="px-3 pt-2.5 pb-1.5">
            <p className="ed-eyebrow">{t("languageUiSection")}</p>
          </div>
          {UI_LANGS.map((l) => (
            <button
              key={l.code}
              role="option"
              aria-selected={lang === l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-neutral-50 border-t border-neutral-100 ${
                lang === l.code ? "bg-neutral-50" : ""
              }`}
            >
              <Flag cc={l.cc} alt={l.label} size={26} />
              <span className="font-serif text-sm flex-1">{l.label}</span>
              {lang === l.code && <Check size={13} strokeWidth={2} className="text-emerald-600 shrink-0" />}
            </button>
          ))}

          <div className="px-3 pt-3 pb-1.5 border-t border-neutral-200 bg-neutral-50/50">
            <p className="ed-eyebrow inline-flex items-center gap-1.5">
              <Globe size={11} strokeWidth={1.6} /> {t("languageAiSection")}
            </p>
            <p className="text-[0.7rem] text-neutral-500 mt-1 leading-snug">
              {t("languageAiHint")}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-0 border-t border-neutral-100">
            {AI_LANGS.map((l) => (
              <div
                key={l.code}
                title={l.label}
                data-testid={`lang-ai-${l.code}`}
                className="flex flex-col items-center gap-1.5 py-3 border-r last:border-r-0 border-neutral-100 text-neutral-700"
              >
                <Flag cc={l.cc} alt={l.label} size={30} />
                <span className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-neutral-500">{l.code}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
